import type { FirebaseError } from 'firebase/app'
import {
  type DataSnapshot,
  get,
  onValue,
  type Query as DatabaseQuery,
} from 'firebase/database'
import {
  type DocumentData,
  DocumentReference,
  DocumentSnapshot,
  getDoc,
  getDocs,
  onSnapshot,
  Query,
  QueryDocumentSnapshot,
  QuerySnapshot,
} from 'firebase/firestore'

export interface ListenerOptions<E extends Error = Error> {
  onError?: (error: E) => void
}

/**
 * Checks if an error is a cancellation error (AbortError or Firestore 'cancelled' code).
 * These errors are typically harmless and occur during component unmounting.
 */
export function isCancellationError(error: Error): boolean {
  return (
    error.name === 'AbortError' ||
    (error as FirebaseError).code === 'cancelled' ||
    error.message?.toLowerCase().includes('aborted')
  )
}

/**
 * Listens to a Firestore collection or query and returns an unsubscribe function.
 */
export function listenToCollection<T = any, R = any>(
  query: Query<DocumentData>,
  onData: (data: R[]) => void,
  options: ListenerOptions<FirebaseError> & {
    map?: (doc: QueryDocumentSnapshot<DocumentData>) => R
  } = {},
) {
  return onSnapshot(
    query,
    (snapshot: QuerySnapshot<DocumentData>) => {
      const data = snapshot.docs.map((doc) => {
        if (options.map) {
          return options.map(doc)
        }
        return {
          id: doc.id,
          ...(doc.data() as T),
        } as unknown as R
      })

      onData(data)
    },
    (error) => {
      if (options.onError) {
        options.onError(error)
      } else {
        if (isCancellationError(error)) return

        console.error('Firestore collection listener error:', error)
      }
    },
  )
}

/**
 * Listens to a Firestore document and returns an unsubscribe function.
 */
export function listenToDocument<T = any, R = any>(
  docRef: DocumentReference<DocumentData>,
  onData: (data: R | null) => void,
  options: ListenerOptions<FirebaseError> & {
    map?: (doc: DocumentSnapshot<DocumentData>) => R
  } = {},
) {
  return onSnapshot(
    docRef,
    (snapshot: DocumentSnapshot<DocumentData>) => {
      if (!snapshot.exists()) {
        onData(null)
        return
      }

      if (options.map) {
        onData(options.map(snapshot))
        return
      }

      const data = {
        id: snapshot.id,
        ...(snapshot.data() as T),
      } as unknown as R

      onData(data)
    },
    (error) => {
      if (options.onError) {
        options.onError(error)
      } else {
        if (isCancellationError(error)) return

        console.error('Firestore document listener error:', error)
      }
    },
  )
}

/**
 * Listens to a Realtime Database reference and returns an unsubscribe function.
 */
export function listenToRTDB<T = any, R = T>(
  query: DatabaseQuery,
  onData: (data: R | null) => void,
  options: ListenerOptions & {
    map?: (snapshot: DataSnapshot) => R
  } = {},
) {
  return onValue(
    query,
    (snapshot: DataSnapshot) => {
      if (!snapshot.exists()) {
        onData(null)
        return
      }

      if (options.map) {
        onData(options.map(snapshot))
        return
      }

      onData(snapshot.val() as R)
    },
    (error) => {
      if (options.onError) {
        options.onError(error)
      } else {
        if (isCancellationError(error)) return

        console.error('RTDB listener error:', error)
      }
    },
  )
}

/**
 * Fetches a Firestore collection once.
 */
export async function getCollection<T = any, R = any>(
  query: Query<DocumentData>,
  options: {
    map?: (doc: QueryDocumentSnapshot<DocumentData>) => R
  } = {},
): Promise<R[]> {
  const snapshot = await getDocs(query)
  return snapshot.docs.map((doc) => {
    if (options.map) {
      return options.map(doc)
    }
    return {
      id: doc.id,
      ...(doc.data() as T),
    } as unknown as R
  })
}

/**
 * Fetches a Firestore document once.
 */
export async function getDocument<T = any, R = any>(
  docRef: DocumentReference<DocumentData>,
  options: {
    map?: (doc: DocumentSnapshot<DocumentData>) => R
  } = {},
): Promise<R | null> {
  const snapshot = await getDoc(docRef)
  if (!snapshot.exists()) {
    return null
  }

  if (options.map) {
    return options.map(snapshot)
  }

  return {
    id: snapshot.id,
    ...(snapshot.data() as T),
  } as unknown as R
}

/**
 * Fetches a Realtime Database value once.
 */
export async function getRTDB<T = any, R = T>(
  query: DatabaseQuery,
  options: {
    map?: (snapshot: DataSnapshot) => R
  } = {},
): Promise<R | null> {
  const snapshot = await get(query)
  if (!snapshot.exists()) {
    return null
  }

  if (options.map) {
    return options.map(snapshot)
  }

  return snapshot.val() as R
}
